import { getSessionJWT } from "@/infra/auth/helper";
import { ManagerOptions, Socket, SocketOptions, io } from "socket.io-client";
import requestConfig from "../../shared/config/request";

const MAX_QUEUE_SIZE = 100;

export const getSocketOptions = (
): Partial<SocketOptions | ManagerOptions> => ({
  transports: ["websocket"],
  upgrade: true,
  rememberUpgrade: false,
  forceNew: true,
  closeOnBeforeunload: false,
  reconnection: true,
  reconnectionAttempts: 3,
  withCredentials: true,
  path: requestConfig.socketPath
});

export let mainSocket: Socket | undefined = undefined;
export let vendorSocket: Socket | undefined = undefined;

const emitQueue: { event: string; args: any[] }[] = [];
let isReconnecting = false;

export const getZaniaSocket = (): Socket => {
  const token = getSessionJWT();

  if (!token) {
    throw new Error(
      "Failed to authenticate. Please try logging in again. Contact Zania support if the issue persists.",
    );
  }

  if (mainSocket) {
    return mainSocket;
  }

  mainSocket = io(`${requestConfig.socketUrl}/`, getSocketOptions());

  // When connected, flush queued emits
  mainSocket.on("connect", () => {
    isReconnecting = false;
    while (emitQueue.length > 0) {
      const { event, args } = emitQueue.shift()!;
      mainSocket?.emit(event, ...args);
    }
  });

  // If reconnect fails, clear the emit queue
  mainSocket.on("reconnect_failed", () => {
  // ...removed console.error...
    emitQueue.length = 0;
    isReconnecting = false;
  });

  return mainSocket;
};

// Vendor socket for third-party vendor forms with HTTP cookie authentication
export const getVendorSocket = (token?: string): Socket => {
  // ...console logs removed for production...
  if (vendorSocket) {
    return vendorSocket;
  }
  try {
    const socketUrl = `${requestConfig.socketUrl}/vendor`;
    const options: Partial<SocketOptions & ManagerOptions> = {
      ...getSocketOptions(),
      forceNew: false, // Don't force new connection for vendor socket
    };
    if (token) {
      (options as any).extraHeaders = {
        'token': token,
      };
    }
    vendorSocket = io(socketUrl, options);
    if (!vendorSocket) {
      throw new Error('Socket.io returned null/undefined socket');
    }
    // Set up error handling at the socket level
    vendorSocket.on('connect_error', (error) => {
      // ...console logs removed...
    });
    // Handle unexpected disconnects
    vendorSocket.on('disconnect', (reason) => {
      // ...console logs removed...
    });
    // Listen for successful connection to the vendor namespace
    vendorSocket.on('connect', () => {
      // ...console logs removed...
    });
    return vendorSocket;
  } catch (error) {
    vendorSocket = undefined; // Reset on error
    throw error;
  }
};
export const safeEmit = (event: string, ...args: any[]) => {
  const socket = getZaniaSocket();

  if (socket.connected) {
    socket.emit(event, ...args);
  } else {
    if (emitQueue.length >= MAX_QUEUE_SIZE) {
      // ...removed console.warn...
      emitQueue.shift(); // Remove the oldest event
    }

  // ...removed console.warn...
    
    emitQueue.push({ event, args });

    if (!isReconnecting) {
      isReconnecting = true;
      socket.connect(); // will trigger queued emit on successful connect
    }
  }
};
